import type { ThreadAuthor } from "@compass/shared/schema/author";
import { Link } from "@tanstack/react-router";
import React, { useMemo, useState } from "react";
import { Avatar, Button, MarkdownContent, ProseDocument } from "components/elements";
import { useUserTimezone } from "hooks/useUserTimezone";
import { formatISODate } from "lib/formatISODate";
import { SocialProfile } from "@compass/shared/types";

interface ActivitiesSectionProps {
    user: SocialProfile;
}

// ThreadAuthor is a discriminated union; only `identified`, `anonymous-self`,
// and `deleted` carry an `avatarUrl`. The two remaining variants
// (`anonymous`, `compass-team`) get a null avatar so the consumer falls back
// to the initials/anonymous SVG path.
function authorAvatarUrl(author: ThreadAuthor): string | null {
    switch (author.type) {
        case "identified":
        case "anonymous-self":
        case "deleted":
            return author.avatarUrl;
        case "anonymous":
        case "compass-team":
            return null;
    }
}

export const ActivitiesSection = ({ user }: ActivitiesSectionProps) => {
    const timeZone = useUserTimezone();
    const [activeTab, setActiveTab] = useState<"posts" | "blogs">("posts");

    const activities = useMemo(() => {
        const postActivities = (user.posts || []).map((post) => ({
            type: "post" as const,
            id: post.id,
            createdAt: post.createdAt,
            authorName: post.author.name,
            authorAvatarUrl: authorAvatarUrl(post.author),
            title: post.title,
        }));

        const commentActivities = (user.postComments || []).map((comment) => {
            const postAuthor = comment.post.author;
            return {
                type: "comment" as const,
                id: comment.id,
                createdAt: comment.createdAt,
                authorName: comment.author.name,
                authorAvatarUrl: authorAvatarUrl(comment.author),
                postId: comment.postId,
                postTitle: comment.post?.title,
                contentJson: comment.contentJson,
                postAuthorName: postAuthor.name,
                // Only link to the post author's profile when they're a live,
                // socially-visible identified user. Anonymous, deleted,
                // compass-team, or social-disabled authors render as plain text.
                postAuthorProfileId:
                    postAuthor.type === "identified" && postAuthor.isSocialEnabled
                        ? postAuthor.id
                        : null,
            };
        });

        return [...postActivities, ...commentActivities].sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
    }, [user.posts, user.postComments]);

    return (
        <div className="bg-brand-polar col-start-1 col-end-4 mb-6 rounded-2xl border border-[#c8c8c8] p-6 shadow-none">
            <div className="text-lg font-semibold">My Activities</div>
            {/*<div className="mt-4 flex items-center">
                <Button
                    variant={activeTab === "posts" ? "primary" : "secondary"}
                    size="smaller"
                    onClick={() => setActiveTab("posts")}
                    className={`mr-2 focus:outline-none focus:ring-0 ${
                        activeTab === "posts"
                            ? "text-white"
                            : "bg-[#f7f7f7] text-gray-700 hover:bg-gray-100"
                    }`}
                >
                    Posts
                </Button>
            </div>*/}
            <div className="mt-4">
                {activeTab === "posts" && (
                    <div className="mt-4 max-h-96 overflow-y-auto">
                        {activities.length > 0 ? (
                            activities.map((activity) => (
                                <div key={`${activity.type}-${activity.id}`}>
                                    <Link
                                        to="/social/post/$id"
                                        className="flex flex-row items-center gap-4 py-4"
                                        params={{
                                            id:
                                                activity.type === "post"
                                                    ? activity.id.toString()
                                                    : activity.postId.toString(),
                                        }}
                                    >
                                        <div className="flex w-full min-w-0 items-center gap-4">
                                            <Avatar
                                                className="h-12 w-12 shrink-0 bg-slate-200 text-xs text-slate-400"
                                                photoUrl={activity.authorAvatarUrl}
                                                name={activity.authorName}
                                            />
                                            <div className="min-w-0 flex-1 overflow-hidden">
                                                <div className="text-sm font-semibold text-gray-700">
                                                    {activity.authorName}
                                                </div>
                                                <div className="text-sm text-gray-700">
                                                    {activity.type === "post" ? (
                                                        <>
                                                            created a{" "}
                                                            <Link
                                                                to="/social/post/$id"
                                                                params={{
                                                                    id: activity.id.toString(),
                                                                }}
                                                                className="text-blue-500"
                                                            >
                                                                post
                                                            </Link>
                                                            <div className="text-xs text-gray-500">
                                                                {formatISODate(
                                                                    activity.createdAt,
                                                                    timeZone,
                                                                )}
                                                            </div>
                                                            <div>
                                                                <MarkdownContent
                                                                    markdown={activity.title}
                                                                />
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            commented on{" "}
                                                            {activity.postAuthorProfileId !==
                                                            null ? (
                                                                <Link
                                                                    to="/profile/$id"
                                                                    params={{
                                                                        id: String(
                                                                            activity.postAuthorProfileId,
                                                                        ),
                                                                    }}
                                                                    onClick={(e) =>
                                                                        e.stopPropagation()
                                                                    }
                                                                    className="text-blue-500"
                                                                >
                                                                    {activity.postAuthorName}
                                                                </Link>
                                                            ) : (
                                                                activity.postAuthorName
                                                            )}
                                                            's{" "}
                                                            <Link
                                                                to="/social/post/$id"
                                                                params={{
                                                                    id: activity.postId.toString(),
                                                                }}
                                                                className="text-blue-500"
                                                            >
                                                                post
                                                            </Link>
                                                            <div className="text-xs text-gray-500">
                                                                {formatISODate(
                                                                    activity.createdAt,
                                                                    timeZone,
                                                                )}
                                                            </div>
                                                            <div>
                                                                <ProseDocument
                                                                    doc={activity.contentJson}
                                                                />
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                </div>
                            ))
                        ) : (
                            <span>No activities available</span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
